import type { ProductVariant } from "@workspace/crystallize/generated/discovery/";
import { FormattedPrice } from "@workspace/ui/components/formatted-price";
import { cn } from "@workspace/ui/lib/utils";
import { useEffect, useState } from "react";
import CrystallizeResponsiveImage from "./crystallize-responsive-image";

type ExtraSelection = { sku: string; quantity?: number };

export default function ProductExtrasSelector({
	extras,
	onSelect,
	selectedExtras,
}: {
	extras: ProductVariant[];
	onSelect: (extras: ProductVariant[]) => void;
	selectedExtras: ProductVariant[];
}) {
	const [localSelectedExtras, setLocalSelectedExtras] = useState<
		ExtraSelection[]
	>([]);

	useEffect(() => {
		onSelect(
			extrasWithSkuAndPrice(extras).filter((extra) =>
				localSelectedExtras.some((e) => e.sku === extra?.sku),
			),
		);
	}, [localSelectedExtras, extras, onSelect]);

	const toggleExtra = ({ sku, quantity }: ExtraSelection) => {
		setLocalSelectedExtras((prev) =>
			prev.some((e) => e.sku === sku)
				? prev.filter((e) => e.sku !== sku)
				: [...prev, { sku, quantity }],
		);
	};

	function hasSkuAndPrice(
		extra: ProductVariant,
	): extra is ProductVariant & { sku: string; defaultPrice: number } {
		return (
			typeof extra?.sku === "string" && typeof extra?.defaultPrice === "number"
		);
	}

	const extrasWithSkuAndPrice = (extras: ProductVariant[]) =>
		extras.filter(hasSkuAndPrice);

	return (
		<>
			<div className="grid grid-cols-1 gap-4">
				{extrasWithSkuAndPrice(extras)?.map((extra) => (
					<div
						key={extra?.sku}
						onClick={() =>
							toggleExtra({ sku: extra?.sku, quantity: extra?.maximumQuantity })
						}
						onKeyDown={(e) => {
							if (e.key === "Enter" || e.key === " ") {
								toggleExtra({
									sku: extra?.sku,
									quantity: extra?.maximumQuantity,
								});
							}
						}}
						className={cn(
							"grid items-center gap-3 rounded-lg border-2 p-3 transition-colors",
							// 👇 72px image, flexible middle, tight price column
							"grid-cols-[4.5rem_1fr_auto]",
							localSelectedExtras.some((e) => e.sku === extra?.sku)
								? "border-primary bg-primary/5"
								: "border-border hover:border-primary/50",
						)}
					>
						{/* Image */}
						{extra?.images?.[0] && (
							<div className="aspect-square h-18 w-18 overflow-hidden rounded-md bg-muted">
								<CrystallizeResponsiveImage
									image={extra.images[0]}
									className="block h-full w-full object-cover"
									sizes="72px"
								/>
							</div>
						)}

						{/* Text (allow shrinking/wrapping) */}
						<div className="min-w-0">
							<label
								htmlFor={extra?.sku}
								className="block cursor-pointer truncate font-medium md:whitespace-normal"
							>
								{extra?.name}
							</label>
							{extra?.product?.brief && (
								<p className="text-muted-foreground text-sm">
									{extra?.product?.brief}
								</p>
							)}
							<input
								type="checkbox"
								name="productExtras"
								value={extra?.sku}
								className="hidden"
								checked={localSelectedExtras.some((e) => e.sku === extra?.sku)}
								onChange={() =>
									toggleExtra({
										sku: extra?.sku,
										quantity: extra?.maximumQuantity,
									})
								}
								aria-labelledby={`${extra?.sku}-title`}
								aria-describedby={`${extra?.sku}-description ${extra?.sku}-price`}
							/>
							<span className="text-muted-foreground text-sm">
								{localSelectedExtras.some((e) => e.sku === extra?.sku)
									? "Lagt til"
									: "Legg til"}
							</span>
						</div>

						{/* Price (tight, right aligned) */}
						<FormattedPrice
							className="justify-self-end font-medium text-lg"
							price={extra?.defaultPrice ?? 0}
						/>
					</div>
				))}
			</div>
		</>
	);
}
